/**
 * One field on the handshake sheet takes both kinds of code, so it has to work
 * out which one is being typed as the characters arrive.
 *
 * Both are drawn from the same narrow alphabet — letters a to h, digits 1 to 8
 * — chosen so nothing in them can be misread when read out loud or written down
 * by hand. What tells them apart is which foot they start on: a member code
 * opens with a letter, a claim code with a digit. That is decided by the very
 * first character, so the field knows from one keystroke whether to wait for
 * four characters or six.
 *
 * A member code is read out and typed back within the minute, and alternates
 * letter and digit so there is less of it to hear wrong. A claim code is
 * carried around on paper for days and takes anything after the first
 * character, which is what lets it be shorter without being easier to guess.
 */

export const MEMBER_CODE_LENGTH = 4;
export const PASSERBY_CODE_LENGTH = 6;

/**
 * A member code's shape with none of its content, for the slot to stand in
 * while it waits for one: the same four characters in the same alternation, so
 * the slot is the width and sits on the baseline a code will before there is a
 * code to put in it. The alphabet is even enough — the digits are tabular and
 * the letters bar `f` are within a pixel or two of each other — that any code
 * lands where this one stood.
 */
export const MEMBER_CODE_STANDIN = 'a1a1';

export interface CodeShape {
  length: number;
  startsWithLetter: boolean;
  alternates: boolean;
}

export const MEMBER_CODE_SHAPE: CodeShape = {
  length: MEMBER_CODE_LENGTH,
  startsWithLetter: true,
  alternates: true,
};

export const PASSERBY_CODE_SHAPE: CodeShape = {
  length: PASSERBY_CODE_LENGTH,
  startsWithLetter: false,
  alternates: false,
};

/** Which kind of code this is turning into, or null if it cannot be either. */
export function codeShape(raw: string): CodeShape | null {
  const first = raw[0];

  if (!first) return null;
  if (/[a-h]/.test(first)) return MEMBER_CODE_SHAPE;
  if (/[1-8]/.test(first)) return PASSERBY_CODE_SHAPE;

  return null;
}

function belongsAt(ch: string, shape: CodeShape, position: number): boolean {
  if (position === 0) {
    return shape.startsWithLetter ? /[a-h]/.test(ch) : /[1-8]/.test(ch);
  }

  if (!shape.alternates) return /[a-h1-8]/.test(ch);

  const wantsLetter = (position % 2 === 0) === shape.startsWithLetter;
  return wantsLetter ? /[a-h]/.test(ch) : /[1-8]/.test(ch);
}

/** Keeps what belongs to the code and drops the rest, up to its full length. */
export function filterCode(raw: string, shape: CodeShape): string {
  let validated = '';

  for (const ch of raw) {
    if (belongsAt(ch, shape, validated.length)) validated += ch;
    if (validated.length >= shape.length) break;
  }

  return validated;
}
